if (!customElements.get('product-size-advisor')) {
  customElements.define(
    'product-size-advisor',
    class ProductSizeAdvisor extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.openBtn = this.querySelector('[data-size-advisor-open]');
        this.modal = this.querySelector('[data-size-advisor-modal]');
        this.closeBtns = Array.from(this.querySelectorAll('[data-size-advisor-close]'));
        this.modalPlaceholder = document.createComment('size-advisor-modal-placeholder');
        this.modalPortaled = false;
        this.heightInput = this.querySelector('[data-size-advisor-height]');
        this.weightInput = this.querySelector('[data-size-advisor-weight]');
        this.fitInput = this.querySelector('[data-size-advisor-fit]');
        this.calcBtn = this.querySelector('[data-size-advisor-calc]');
        this.result = this.querySelector('[data-size-advisor-result]');
        this.sizeOut = this.querySelector('[data-size-advisor-size]');
        this.confidenceOut = this.querySelector('[data-size-advisor-confidence]');
        this.hintOut = this.querySelector('[data-size-advisor-hint]');
        this.applyBtn = this.querySelector('[data-size-advisor-apply]');
        this.availableOut = this.querySelector('[data-size-advisor-available]');

        this.config = this.parseJson('[data-size-advisor-config]', {});
        this.optionNames = this.parseJson('[data-size-advisor-options]', []);
        this.variants = this.parseJson('[data-size-advisor-variants]', []);

        this.sizeOptionIndex = this.detectSizeOptionIndex();
        this.availableSizes = this.getAvailableSizes();
        this.lastRecommendedSize = '';

        if (this.modal) {
          this.modal.hidden = true;
          this.modal.classList.remove('is-open');
        }

        this.renderAvailableSizes();
        this.restoreInputs();

        this.onCalculate = this.onCalculate.bind(this);
        this.onApply = this.onApply.bind(this);
        this.onInput = this.onInput.bind(this);
        this.onOpen = this.onOpen.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onEsc = this.onEsc.bind(this);

        this.calcBtn?.addEventListener('click', this.onCalculate);
        this.applyBtn?.addEventListener('click', this.onApply);
        this.heightInput?.addEventListener('input', this.onInput);
        this.weightInput?.addEventListener('input', this.onInput);
        this.fitInput?.addEventListener('change', this.onInput);
        this.openBtn?.addEventListener('click', this.onOpen);
        this.closeBtns.forEach((btn) => btn.addEventListener('click', this.onClose));
        document.addEventListener('keydown', this.onEsc);
      }

      disconnectedCallback() {
        this.calcBtn?.removeEventListener('click', this.onCalculate);
        this.applyBtn?.removeEventListener('click', this.onApply);
        this.heightInput?.removeEventListener('input', this.onInput);
        this.weightInput?.removeEventListener('input', this.onInput);
        this.fitInput?.removeEventListener('change', this.onInput);
        this.openBtn?.removeEventListener('click', this.onOpen);
        this.closeBtns.forEach((btn) => btn.removeEventListener('click', this.onClose));
        document.removeEventListener('keydown', this.onEsc);
      }

      parseJson(selector, fallback) {
        const node = this.querySelector(selector);
        if (!node) return fallback;
        try {
          return JSON.parse(node.textContent || '');
        } catch (e) {
          return fallback;
        }
      }

      detectSizeOptionIndex() {
        const preferred = (this.config.sizeOptionName || 'Size').trim().toLowerCase();
        const normalized = this.optionNames.map((name) => String(name || '').trim().toLowerCase());
        const exact = normalized.findIndex((name) => name === preferred);
        if (exact >= 0) return exact;

        const keywords = ['size', '尺码', '尺寸', 'tamanho', 'taglia'];
        const fuzzy = normalized.findIndex((name) => keywords.some((keyword) => name.includes(keyword)));
        return fuzzy >= 0 ? fuzzy : 0;
      }

      getAvailableSizes() {
        const raw = this.variants
          .filter((v) => v && v.available)
          .map((v) => v[`option${this.sizeOptionIndex + 1}`])
          .filter(Boolean);
        const unique = Array.from(new Set(raw.map((v) => String(v).trim())));
        return unique.sort((a, b) => this.sizeRank(a) - this.sizeRank(b));
      }

      sizeRank(size) {
        const key = String(size || '')
          .trim()
          .toUpperCase();
        const map = {
          XXS: -2,
          XS: -1,
          S: 0,
          M: 1,
          L: 2,
          XL: 3,
          XXL: 4,
          '2XL': 4,
          XXXL: 5,
          '3XL': 5,
          '4XL': 6,
          '5XL': 7,
        };
        if (Object.prototype.hasOwnProperty.call(map, key)) return map[key];
        return 100 + key.charCodeAt(0);
      }

      renderAvailableSizes() {
        if (!this.availableOut) return;
        if (!this.availableSizes.length) {
          this.availableOut.hidden = true;
          return;
        }
        this.availableOut.hidden = false;
        this.availableOut.textContent = `Available sizes: ${this.availableSizes.join(' / ')}`;
      }

      onInput() {
        this.saveInputs();
      }

      onOpen() {
        if (!this.modal) return;
        if (!this.modalPortaled) {
          this.modal.parentNode?.insertBefore(this.modalPlaceholder, this.modal);
          document.body.appendChild(this.modal);
          this.modalPortaled = true;
        }
        this.modal.hidden = false;
        this.modal.classList.add('is-open');
        document.body.classList.add('overflow-hidden');
      }

      onClose() {
        if (!this.modal) return;
        this.modal.classList.remove('is-open');
        this.modal.hidden = true;
        document.body.classList.remove('overflow-hidden');
        if (this.modalPortaled && this.modalPlaceholder.parentNode) {
          this.modalPlaceholder.parentNode.insertBefore(this.modal, this.modalPlaceholder);
          this.modalPlaceholder.parentNode.removeChild(this.modalPlaceholder);
          this.modalPortaled = false;
          this.modalPlaceholder = document.createComment('size-advisor-modal-placeholder');
        }
      }

      onEsc(event) {
        if (event.key !== 'Escape') return;
        if (!this.modal || this.modal.hidden) return;
        this.onClose();
      }

      onCalculate() {
        if (!this.availableSizes.length) return;

        const height = parseFloat(this.heightInput?.value || '0');
        const weight = parseFloat(this.weightInput?.value || '0');
        const fit = this.fitInput?.value || 'regular';

        const recommendation = this.calculateRecommendation(height, weight, fit);
        if (!recommendation) return;

        this.lastRecommendedSize = recommendation.size;
        this.result.hidden = false;
        this.sizeOut.textContent = recommendation.size;
        this.confidenceOut.textContent = recommendation.confidence;
        this.hintOut.textContent = recommendation.hint;
        this.saveInputs();
      }

      calculateRecommendation(height, weight, fit) {
        if (!Number.isFinite(height) || !Number.isFinite(weight) || height <= 0 || weight <= 0) {
          this.result.hidden = false;
          this.sizeOut.textContent = '--';
          this.confidenceOut.textContent = '';
          this.hintOut.textContent = 'Enter both height and weight to calculate your recommended size.';
          return null;
        }

        let score = 1;
        score += (height - 170) / 8;
        score += (weight - 68) / 10;

        if (fit === 'slim') score -= 0.35;
        if (fit === 'relaxed') score += 0.45;

        const title = String(this.config.title || '').toLowerCase();
        if (title.includes('player version') || title.includes('球员版')) score += 0.2;
        if (title.includes('fan version') || title.includes('球迷版')) score -= 0.1;

        const sorted = this.availableSizes.slice().sort((a, b) => this.sizeRank(a) - this.sizeRank(b));
        let best = sorted[0];
        let bestDistance = Number.POSITIVE_INFINITY;
        sorted.forEach((size) => {
          const distance = Math.abs(this.sizeRank(size) - score);
          if (distance < bestDistance) {
            bestDistance = distance;
            best = size;
          }
        });

        let confidence = 'Medium confidence';
        if (bestDistance <= 0.35) confidence = 'High confidence';
        if (bestDistance >= 1) confidence = 'Low confidence';

        let hint = 'Between sizes? choose one size up for comfort.';
        if (fit === 'slim') hint = 'For a tighter on-body look, keep this size.';
        if (fit === 'relaxed') hint = 'For extra room, one size up can work well.';

        return { size: best, confidence, hint };
      }

      onApply() {
        if (!this.lastRecommendedSize) return;
        const productInfo = this.closest('product-info');
        const variantSelects = productInfo?.querySelector('variant-selects');
        if (!variantSelects) return;

        const optionName = this.optionNames[this.sizeOptionIndex] || this.config.sizeOptionName || 'Size';
        const escapedOptionName = this.escapeCss(optionName);
        const optionPosition = this.sizeOptionIndex + 1;
        const radioName = `${optionName}-${optionPosition}`;

        const radio = variantSelects.querySelector(
          `input[type="radio"][name="${this.escapeCss(radioName)}"][value="${this.escapeCss(this.lastRecommendedSize)}"]`
        );

        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          this.onClose();
          return;
        }

        const select = variantSelects.querySelector(`select[name="options\\[${escapedOptionName}\\]"]`);
        if (select) {
          select.value = this.lastRecommendedSize;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          this.onClose();
        }
      }

      escapeCss(value) {
        if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(String(value));
        return String(value).replace(/["\\]/g, '\\$&');
      }

      saveInputs() {
        const key = `size-advisor:${this.sectionId}`;
        const payload = {
          h: this.heightInput?.value || '',
          w: this.weightInput?.value || '',
          f: this.fitInput?.value || 'regular',
        };
        try {
          localStorage.setItem(key, JSON.stringify(payload));
        } catch (e) {}
      }

      restoreInputs() {
        const key = `size-advisor:${this.sectionId}`;
        try {
          const raw = localStorage.getItem(key);
          if (!raw) return;
          const data = JSON.parse(raw);
          if (this.heightInput && data.h) this.heightInput.value = data.h;
          if (this.weightInput && data.w) this.weightInput.value = data.w;
          if (this.fitInput && data.f) this.fitInput.value = data.f;
        } catch (e) {}
      }
    }
  );
}
